import {
  Badge,
  Drawer,
  Group,
  Select,
  Stack,
  Text,
  TextInput,
  Title,
  useMantineTheme,
} from "@mantine/core"
import { Dropzone } from "@mantine/dropzone"
import { useForm, zodResolver } from "@mantine/form"
import { notifications } from "@mantine/notifications"
import { IconFileCertificate, IconUpload, IconX } from "@tabler/icons-react"
import { Button, CountrySelector } from "@tfg/ui"
import { AxiosError } from "axios"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { SupplierResponses } from "@tfg/types/endpoints/supplier"
import {
  prodSiteSchema,
  prodSiteValidationType,
} from "@tfg/types/schemas/productionSites"

import { queryClient } from "~/backend/queryClient"
import { useEditProductionSiteMutation } from "~/pages/SupplierPortal/ProductionSites/hooks/useEditProductionSiteMutation.ts"
import { useListCertificateTypesQuery } from "~/pages/SupplierPortal/ProductionSites/hooks/useListCertificteTypesQuery.ts"
import { useUploadMutation } from "~/pages/SupplierPortal/TraceabilityRequests/hooks/useUploadMutation"
import { COUNTRIES } from "~/utils/countries.ts"

interface Props {
  productionSite: SupplierResponses.ProductionSite
  onClose: () => void
}

export const EditProductionSiteDrawer = ({
  productionSite,
  onClose,
}: Props) => {
  const { colors } = useMantineTheme()

  const { t } = useTranslation(["supplier"])
  const { data: certificateTypes } = useListCertificateTypesQuery()
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])

  const { mutate, isLoading } = useEditProductionSiteMutation(
    productionSite.id,
    {
      onSuccess: () => {
        handleClose()
      },
      onError: (error: AxiosError) => {
        notifications.show({
          title: "dup",
          message: error.message,
          color: "red",
        })
      },
    }
  )

  const { mutate: uploadCertificate } = useUploadMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["listProductionSites"],
      })
    },
  })

  const form = useForm<prodSiteValidationType>({
    validate: zodResolver(prodSiteSchema),
    initialValues: {
      name: productionSite.name,
      address: productionSite.address,
      countryIso3166: productionSite.country,
      certificates: [],
    },
  })

  const onSubmit = (values: prodSiteValidationType) => {
    const certificates = selectedFiles
      .map((file, index) => ({
        file,
        typeId: values.certificates[index].type,
      }))
      .filter(({ typeId }) => typeId)
    mutate({
      request: values,
    })
    for (const certificate of certificates) {
      uploadCertificate({
        file: certificate.file,
        typeId: certificate.typeId,
        productionSiteId: productionSite.id,
      })
    }
  }

  const handleClose = () => {
    form.reset()
    onClose()
  }

  return (
    <Drawer
      opened
      position="right"
      onClose={handleClose}
      title={<Title order={3}>{t("productionSiteModal.editTitle")}</Title>}
      size="xl"
    >
      <form onSubmit={form.onSubmit((values) => onSubmit(values))}>
        <Stack gap="xl">
          <Stack gap="sm">
            <Title order={4}>
              {t("productionSiteModal.titles.information")}
            </Title>
            <TextInput
              {...form.getInputProps("name")}
              label={t("productionSiteModal.labels.name")}
            />
            <TextInput
              {...form.getInputProps("address")}
              label={t("productionSiteModal.labels.address")}
            />
            <CountrySelector
              onChange={form.getInputProps("countryIso3166").onChange}
              value={form.getInputProps("countryIso3166").value}
              label={t("productionSiteModal.labels.country")}
              searchable
              countriesMapper={COUNTRIES}
            />
          </Stack>
          <Stack gap="sm">
            <Title order={4}>
              {t("productionSiteModal.titles.certificate")}
            </Title>
            <Group>
              <Text size="sm">
                {t("productionSiteModal.certificatesCount", {
                  count: productionSite.certificates.length,
                })}
              </Text>
              {productionSite.certificates.map((c) => (
                <Badge key={c.id} size="md" color="gray">
                  {c.type}
                </Badge>
              ))}
            </Group>
            <Dropzone
              onDrop={(newFiles) => {
                newFiles.forEach(() =>
                  form.insertListItem("certificates", { type: null })
                )
                setSelectedFiles((files) => {
                  return [...files, ...newFiles]
                })
              }}
              w="100%"
            >
              <Group justify="center">
                <Dropzone.Accept>
                  <IconUpload color={colors.green[6]} />
                </Dropzone.Accept>
                <Dropzone.Reject>
                  <IconX color={colors.red[6]} />
                </Dropzone.Reject>
                <Dropzone.Idle>
                  <IconFileCertificate />
                </Dropzone.Idle>
                <Text>{t("productionSiteModal.actions.addCertificate")}</Text>
              </Group>
            </Dropzone>
            <Group justify="space-between" w="100%">
              <Stack gap="xs" justify="center" w="100%">
                <Stack gap="xs" w="100%">
                  {selectedFiles.map((file, index) => (
                    <Group
                      key={index}
                      justify="space-between"
                      w="100%"
                      wrap="nowrap"
                    >
                      <Text mt={24} size="sm">
                        {file.name}
                      </Text>
                      <Group align="center" wrap="nowrap">
                        <Select
                          w={300}
                          searchable
                          label="Type"
                          data={
                            certificateTypes
                              ?.filter((c) => c.category !== "TRANSACTION")
                              .map((type) => ({
                                label: type.name,
                                value: type.id.toString(),
                              })) ?? []
                          }
                          {...form.getInputProps(`certificates.${index}.type`)}
                        />
                        <Button
                          size="xs"
                          variant="tertiary-critical"
                          onClick={() => {
                            form.removeListItem("certificates", index)
                            setSelectedFiles((files) => {
                              return files.filter((_file, i) => index !== i)
                            })
                          }}
                        >
                          {t("productionSiteModal.actions.remove")}
                        </Button>
                      </Group>
                    </Group>
                  ))}
                </Stack>
              </Stack>
            </Group>
          </Stack>
          <Group justify="right">
            <Button variant="tertiary" onClick={handleClose}>
              {t("productionSiteModal.actions.cancel")}
            </Button>
            <Button type="submit" loading={isLoading}>
              {t("productionSiteModal.actions.create")}
            </Button>
          </Group>
        </Stack>
      </form>
    </Drawer>
  )
}
